import { apiRequest } from '../lib/api-client';

/** The tenant's own lists (context.md sections 34, 101). */

export interface CatalogueEntry {
  id: string;
  name: string;
  code: string;
  description: string | null;
  color: string;
  icon: string | null;
  position: number;
  is_default?: boolean;
  is_active: boolean;
}

export interface SaveEntry {
  name: string;
  code?: string;
  description?: string | null;
  color?: string;
  icon?: string | null;
  position?: number;
  isDefault?: boolean;
  isActive?: boolean;
}

/**
 * One client for five lists.
 *
 * They are the same idea on the server — a named entry with a code, a colour and a switch — so they
 * are the same idea here. The path is the only difference.
 */
export type CataloguePath = 'customer-statuses' | 'customer-sources' | 'loss-reasons';

const KEY: Record<CataloguePath, string> = {
  'customer-statuses': 'statuses',
  'customer-sources': 'sources',
  'loss-reasons': 'reasons',
};

export async function listCatalogue(path: CataloguePath): Promise<CatalogueEntry[]> {
  const body = await apiRequest<Record<string, CatalogueEntry[]>>(`/v1/catalogues/${path}`);

  return body[KEY[path]] ?? [];
}

export function createEntry(path: CataloguePath, input: SaveEntry): Promise<CatalogueEntry> {
  return apiRequest(`/v1/catalogues/${path}`, { method: 'POST', body: input });
}

export function updateEntry(
  path: CataloguePath,
  id: string,
  input: Partial<SaveEntry>,
): Promise<CatalogueEntry> {
  return apiRequest(`/v1/catalogues/${path}/${id}`, { method: 'PATCH', body: input });
}

export function deleteEntry(path: CataloguePath, id: string): Promise<void> {
  return apiRequest(`/v1/catalogues/${path}/${id}`, { method: 'DELETE' });
}

export interface TagEntry {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  color: string;
  is_active: boolean;
  usage: number;
}

export function listTags(): Promise<{ tags: TagEntry[] }> {
  return apiRequest('/v1/catalogues/tags');
}

export function createTag(input: {
  name: string;
  slug?: string;
  description?: string | null;
  color?: string;
}): Promise<TagEntry> {
  return apiRequest('/v1/catalogues/tags', { method: 'POST', body: input });
}

export function updateTag(
  id: string,
  input: { name?: string; description?: string | null; color?: string; isActive?: boolean },
): Promise<TagEntry> {
  return apiRequest(`/v1/catalogues/tags/${id}`, { method: 'PATCH', body: input });
}

export function deleteTag(id: string): Promise<void> {
  return apiRequest(`/v1/catalogues/tags/${id}`, { method: 'DELETE' });
}

export interface ScoreFactor {
  id: string;
  key: string;
  name: string;
  category: string;
  description: string | null;
  default_points: number;
  is_active: boolean;
}

export interface ScoreRule {
  id: string;
  factor_id: string;
  factor_key: string;
  name: string;
  entity_type: string;
  condition: Record<string, unknown>;
  points: number | null;
  position: number;
  is_active: boolean;
}

export function getScoreConfig(): Promise<{ factors: ScoreFactor[]; rules: ScoreRule[] }> {
  return apiRequest('/v1/catalogues/score');
}

export function createFactor(input: {
  name: string;
  key?: string;
  category?: string;
  description?: string | null;
  defaultPoints?: number;
}): Promise<ScoreFactor> {
  return apiRequest('/v1/catalogues/score/factors', { method: 'POST', body: input });
}

export function updateFactor(
  id: string,
  input: { name?: string; category?: string; defaultPoints?: number; isActive?: boolean },
): Promise<ScoreFactor> {
  return apiRequest(`/v1/catalogues/score/factors/${id}`, { method: 'PATCH', body: input });
}

export function deleteFactor(id: string): Promise<void> {
  return apiRequest(`/v1/catalogues/score/factors/${id}`, { method: 'DELETE' });
}

export interface CustomFieldView {
  id: string;
  entity_type: string;
  key: string;
  label: string;
  field_type: string;
  options: Record<string, unknown>;
  required: boolean;
  position: number;
  archived_at: string | null;
}

export function listCustomFields(entity: string): Promise<{ fields: CustomFieldView[] }> {
  return apiRequest(`/v1/crm/custom-fields?entityType=${entity}`);
}

export interface PipelineStage {
  id: string;
  name: string;
  position: number;
  probability: number;
  is_won: boolean;
  is_lost: boolean;
  sla_days: number | null;
}

export interface Pipeline {
  id: string;
  name: string;
  is_default: boolean;
  stages: PipelineStage[];
}

export function listPipelines(): Promise<{ pipelines: Pipeline[] }> {
  return apiRequest('/v1/crm/pipelines');
}

export function createPipeline(input: {
  name: string;
  stages: { name: string; probability?: number; isWon?: boolean; isLost?: boolean }[];
}): Promise<{ id: string }> {
  return apiRequest('/v1/crm/pipelines', { method: 'POST', body: input });
}

export function updatePipeline(
  id: string,
  input: { name?: string; isDefault?: boolean },
): Promise<void> {
  return apiRequest(`/v1/crm/pipelines/${id}`, { method: 'PATCH', body: input });
}

/** The stages as a set: reordering and renaming happen together on the screen that edits them. */
export function replaceStages(
  id: string,
  stages: {
    id?: string;
    name: string;
    probability?: number;
    isWon?: boolean;
    isLost?: boolean;
  }[],
): Promise<void> {
  return apiRequest(`/v1/crm/pipelines/${id}/stages`, { method: 'PUT', body: { stages } });
}
